// Cálculos de la vista Resumen de Reportes Recepción y de "Estadísticas del bot", que es la misma
// vista filtrada por la referencia marcada como bot (referencias.es_bot, V226): asistencia y
// conversión por tipo, resultado de la atención, motivos de no avance y turnos atendidos por día,
// como en la planilla de Excel del estudio. Funciones puras: la pantalla y la exportación a
// PDF/Excel usan exactamente los mismos números.
import { COLOR } from './tokens';
import {
    CLAVE_SIN_CLASIFICAR, CLAVE_TRAMITE_EN_CURSO, asignarColoresResultado, filasResultadoAtencion,
} from './calculosReportes';
import { diasDelRango, esFinDeSemana, fechaCorta } from './fechasReportes';
import { conversionGlobal } from './selectors';
import { graficoBarras, graficoDona } from './exportacionTablas';

const lista = (valor) => (Array.isArray(valor) ? valor : []);

/** Orden de las columnas de resultado en la planilla "Turnos atendidos por día" del estudio. */
const ORDEN_PLANILLA = ['Inicia ahora', 'Carpeta a futuro', 'No quiso dejar carpeta', 'No viable'];

/** Porcentaje con 1 decimal; null si no hay base. */
const pct = (parte, total) => (total > 0 ? Math.round((parte / total) * 1000) / 10 : null);

const r1 = (valor) => {
    if (valor === null || valor === undefined || valor === '') return null;
    const numero = Number(valor);
    return Number.isNaN(numero) ? null : Math.round(numero * 10) / 10;
};

// La base de la conversión: las atenciones finalizadas (la misma del resultado de la atención).
const sumaFinalizadas = (conversion) => lista(conversion).reduce((acc, f) => acc + (f.totalTurnos || 0), 0);
const sumaConTramite = (conversion) => lista(conversion).reduce((acc, f) => acc + (f.conTramite || 0), 0);

/**
 * @typedef {{resumen?: object, conversionPorTipo?: Array, resultadoAtencion?: Array,
 *   motivosNoAvance?: Array, porDiaResultado?: Array}} DatosVista
 */

/**
 * Indicadores del período.
 * @param {DatosVista} datos
 * @returns {Array<{etiqueta: string, valor: number|null, esPorcentaje?: boolean}>}
 */
export function indicadoresVista(datos = {}) {
    const d = datos || {};
    const conTramite = sumaConTramite(d.conversionPorTipo);
    return [
        { etiqueta: 'Turnos del período', valor: d.resumen?.totalTurnos ?? 0 },
        { etiqueta: 'Se presentaron', valor: d.resumen?.turnosAtendidos ?? 0 },
        { etiqueta: 'No vinieron', valor: d.resumen?.turnosNoAsistio ?? 0 },
        { etiqueta: 'Cancelaron', valor: d.resumen?.turnosCancelados ?? 0 },
        { etiqueta: 'Trámite abierto', valor: conTramite },
        { etiqueta: 'Conversión (%)', valor: pct(conTramite, sumaFinalizadas(d.conversionPorTipo)), esPorcentaje: true },
    ];
}

/** Variación porcentual contra el período anterior; null si no hay base para comparar. */
const deltaPct = (actual, anterior) => {
    if (actual == null || anterior == null || Number(anterior) === 0) return null;
    return ((Number(actual) - Number(anterior)) / Number(anterior)) * 100;
};

/**
 * Tarjetas principales (KpiRow): las mismas de Estadísticas Pro, con la variación contra el período
 * anterior, pero sin metas ni espera media.
 * @param {DatosVista} datos
 * @param {{resumen?: object, conversionPorTipo?: Array}|null} anteriores null si no se pudo comparar
 * @param {{sinComparacion?: boolean}} opciones sinComparacion: no hay período anterior comparable (empieza
 *   antes del primer turno del sistema) y la tarjeta lo dice en vez de quedar muda.
 */
export function tarjetasKpi(datos = {}, anteriores = null, opciones = {}) {
    const resumen = datos?.resumen;
    if (!resumen) return [];
    const anterior = anteriores?.resumen || null;
    const conversion = conversionGlobal(datos.conversionPorTipo);
    const conversionAnterior = anteriores ? conversionGlobal(anteriores.conversionPorTipo) : null;

    const dTurnos = deltaPct(resumen.totalTurnos, anterior?.totalTurnos);
    const dAsistencia = deltaPct(resumen.tasaAsistencia, anterior?.tasaAsistencia);
    const dConversion = deltaPct(conversion, conversionAnterior);
    const dCancelados = deltaPct(resumen.turnosCancelados, anterior?.turnosCancelados);

    // Las cancelaciones anteriores a V217 no tienen origen registrado: se dicen aparte.
    const porBot = resumen.canceladosPorBot ?? 0;
    const manual = resumen.canceladosManual ?? 0;
    const sinRegistro = Math.max(0, (resumen.turnosCancelados ?? 0) - porBot - manual);
    const partes = [];
    if (porBot) partes.push(`${porBot} por el BOT`);
    if (manual) partes.push(`${manual} del estudio`);
    if (sinRegistro) partes.push(`${sinRegistro} sin registrar`);

    // Gente nueva: de los que se presentaron, cuántos no tenían rastro previo en el sistema (V229).
    // Es la lectura que le sirve al estudio; los que ya eran clientes van en la ayuda.
    const nuevos = resumen.turnosClientesNuevos ?? null;
    const yaClientes = resumen.turnosYaEranClientes ?? 0;
    const dNuevos = deltaPct(nuevos, anterior?.turnosClientesNuevos);

    return [
        {
            label: 'Turnos del período', value: (resumen.totalTurnos ?? 0).toLocaleString('es-AR'),
            deltaPct: dTurnos, isGood: dTurnos == null ? null : dTurnos >= 0,
            hint: anterior
                ? `vs. ${(anterior.totalTurnos ?? 0).toLocaleString('es-AR')} anterior`
                : (opciones.sinComparacion ? 'sin período anterior comparable' : null),
        },
        {
            // Sin turnos resueltos la asistencia es "sin datos", no 0 %.
            label: 'Asistencia', value: resumen.tasaAsistencia != null ? Number(resumen.tasaAsistencia).toFixed(1) : '—',
            unit: resumen.tasaAsistencia != null ? '%' : '',
            deltaPct: dAsistencia, isGood: dAsistencia == null ? null : dAsistencia >= 0,
            hint: 'de los turnos ya resueltos',
        },
        {
            label: 'Conversión a trámite', value: conversion != null ? conversion.toFixed(1) : '—', unit: '%',
            deltaPct: dConversion, isGood: dConversion == null ? null : dConversion >= 0,
            hint: 'de las atenciones finalizadas',
        },
        ...(nuevos === null ? [] : [{
            label: 'Clientes nuevos', value: nuevos.toLocaleString('es-AR'),
            deltaPct: dNuevos, isGood: dNuevos == null ? null : dNuevos >= 0,
            hint: `de los que se presentaron · ${yaClientes.toLocaleString('es-AR')} ya eran clientes`,
        }]),
        {
            label: 'Cancelaciones', value: (resumen.turnosCancelados ?? 0).toLocaleString('es-AR'),
            deltaPct: dCancelados, isGood: dCancelados == null ? null : dCancelados <= 0,
            hint: partes.length ? partes.join(' · ') : 'ninguna en el período',
        },
    ];
}

/** Conversión por tipo de turno, ordenada por las atenciones finalizadas (la base de la conversión). */
export function filasConversion(datos = {}, formatearTipo = (t) => t) {
    return lista(datos?.conversionPorTipo).map((f) => ({
        tipo: f.tipoTramite,
        etiqueta: formatearTipo(f.tipoTramite),
        atenciones: f.totalTurnos || 0,
        conTramite: f.conTramite || 0,
        conversion: pct(f.conTramite || 0, f.totalTurnos || 0),
    })).sort((a, b) => b.atenciones - a.atenciones);
}

/** Motivos de no avance con el % sobre el total de motivos, de mayor a menor. */
export function filasMotivos(datos = {}) {
    const motivos = lista(datos?.motivosNoAvance);
    const suma = motivos.reduce((acc, f) => acc + (f.cantidad || 0), 0);
    return motivos.map((f) => ({
        motivo: f.motivo,
        etiqueta: f.motivoLabel || f.motivo,
        cantidad: f.cantidad || 0,
        porcentaje: pct(f.cantidad || 0, suma),
    })).sort((a, b) => b.cantidad - a.cantidad);
}

/**
 * "Turnos atendidos por día": una fila por día del período (también los días en 0, como en la
 * planilla) con los atendidos y cuántos terminaron en cada resultado de atención.
 * @param {Array<{fecha, atendidos, resultados}>} porDiaResultado
 * @param {string} desde ISO; sin rango se usan solo los días con datos
 * @param {string} hasta ISO
 */
export function tablaPorDia(porDiaResultado, desde, hasta) {
    const datos = lista(porDiaResultado);
    const porFecha = new Map(datos.map((d) => [d.fecha, d]));
    const totales = new Map();
    const etiquetas = new Map();
    datos.forEach((d) => lista(d.resultados).forEach((r) => {
        totales.set(r.resultado, (totales.get(r.resultado) || 0) + (r.cantidad || 0));
        etiquetas.set(r.resultado, r.resultadoLabel || r.resultado);
    }));
    // Columnas en el orden de la planilla del estudio: los resultados de siempre primero, en
    // orden fijo; después los que se hayan agregado al catálogo (por cantidad) y al final "Vino por
    // un trámite en curso" y "Sin clasificar". Ordenar todo por cantidad movía las columnas de un
    // período a otro.
    const posicion = (clave) => {
        if (clave === CLAVE_SIN_CLASIFICAR) return 99;
        if (clave === CLAVE_TRAMITE_EN_CURSO) return 98;
        const fija = ORDEN_PLANILLA.indexOf(clave);
        return fija >= 0 ? fija : 10;
    };
    const claves = [...totales.keys()].sort((a, b) => posicion(a) - posicion(b) || totales.get(b) - totales.get(a));
    const colores = asignarColoresResultado(claves);

    const fechas = desde && hasta ? diasDelRango(desde, hasta) : datos.map((d) => d.fecha);
    const filas = fechas.map((fecha) => {
        const dia = porFecha.get(fecha);
        return {
            fecha,
            etiqueta: fechaCorta(fecha),
            finDeSemana: esFinDeSemana(fecha),
            atendidos: dia?.atendidos || 0,
            valores: claves.map((clave) => lista(dia?.resultados).find((r) => r.resultado === clave)?.cantidad || 0),
        };
    });

    return {
        resultados: claves.map((clave) => ({ clave, etiqueta: etiquetas.get(clave), color: colores.get(clave) })),
        filas,
        totales: {
            atendidos: filas.reduce((acc, f) => acc + f.atendidos, 0),
            valores: claves.map((_, i) => filas.reduce((acc, f) => acc + f.valores[i], 0)),
        },
    };
}

// ── Exportación ──────────────────────────────────────────────────────────────

const seccion = (datos) => ({ nota: null, vacio: null, grafico: null, ...datos });

/**
 * Estructura de exportación del Resumen (config.vista = 'simple') o de Estadísticas del bot
 * (config.vista = 'bot'), con la misma forma que construirReporteRecepcion (filtros, kpis, alertas,
 * pestanas) para que exportacionPdf.js y ReportesExcel.js la recorran igual.
 * @param {{vista?: 'simple'|'bot', datos: DatosVista, nombreBot?: string, desde?: string, hasta?: string,
 *   filtros?: {periodo?: string, tipoTramite?: string}, formatearTipo?: Function}} config
 */
export function construirReporteSimple(config = {}) {
    const formatearTipo = config.formatearTipo || ((t) => t || '—');
    const datos = config.datos || {};
    const soloBot = config.vista === 'bot';
    const nombreBot = config.nombreBot || 'BOT';
    const filtros = config.filtros || {};

    const conversion = filasConversion(datos, formatearTipo);
    const resultado = filasResultadoAtencion(lista(datos.resultadoAtencion));
    const motivos = filasMotivos(datos);
    const porDia = tablaPorDia(datos.porDiaResultado, config.desde, config.hasta);

    return {
        filtros: [
            ['Período', filtros.periodo || '—'],
            ['Tipo de trámite', filtros.tipoTramite || 'Todos'],
            ['Tipo de visita', filtros.tipoVisita || 'Todas'],
            ['Turnos', soloBot ? `Solo los del bot (referencia "${nombreBot}")` : 'Todos'],
        ],
        kpis: indicadoresVista(datos).map((i) => [i.etiqueta, i.valor]),
        alertas: [],
        pestanas: [{
            id: soloBot ? 'bot' : 'simple',
            titulo: soloBot ? `Estadísticas del bot (${nombreBot})` : 'Resumen de recepción',
            nota: soloBot ? `Solo turnos con la referencia ${nombreBot}.` : null,
            secciones: [
                seccion({
                    id: 'conversionTipo', hoja: 'Conversión por tipo', titulo: 'Conversión por tipo de trámite',
                    nota: 'Por tipo de turno. Conversión = trámites abiertos en la atención sobre las atenciones finalizadas. No cuentan los tipos de turno que no son atención al público (cobros, turnos médicos...) ni quien vino por un trámite que ya tenía en curso.',
                    columnas: ['Tipo de trámite', 'Atenciones', 'Trámite abierto', 'Conversión (%)'],
                    filas: conversion.map((f) => [f.etiqueta, f.atenciones, f.conTramite, f.conversion]),
                    grafico: conversion.length
                        ? graficoBarras(conversion.map((f) => f.etiqueta), [
                            { label: 'Atenciones', data: conversion.map((f) => f.atenciones), backgroundColor: COLOR.azulClaro },
                            { label: 'Trámite abierto', data: conversion.map((f) => f.conTramite), backgroundColor: COLOR.verde },
                        ])
                        : null,
                }),
                seccion({
                    id: 'resultado', hoja: 'Resultado de la atención', titulo: 'Resultado de la atención',
                    nota: 'Sobre las atenciones finalizadas. Inicia ahora + Carpeta a futuro = atenciones en las que se abrió un trámite. "Sin clasificar" = no se cargó el resultado.',
                    columnas: ['Resultado', 'Atenciones', '% del total'],
                    filas: resultado.map((f) => [f.resultadoLabel || f.resultado, f.cantidad, r1(f.porcentaje)]),
                    grafico: resultado.length
                        ? graficoDona(resultado.map((f) => f.resultadoLabel || f.resultado), resultado.map((f) => f.cantidad), resultado.map((f) => f.color))
                        : null,
                }),
                seccion({
                    id: 'motivos', hoja: 'Motivos de no avance', titulo: 'Motivos de no avance',
                    columnas: ['Motivo', 'Cantidad', '% del total'],
                    filas: motivos.map((f) => [f.etiqueta, f.cantidad, f.porcentaje]),
                }),
                seccion({
                    id: 'porDia', hoja: 'Turnos por día', titulo: 'Turnos atendidos por día',
                    columnas: ['Día', 'Atendidos', ...porDia.resultados.map((r) => r.etiqueta)],
                    filas: [
                        ...porDia.filas.map((f) => [f.etiqueta, f.atendidos, ...f.valores]),
                        ...(porDia.filas.length > 1 ? [['Total', porDia.totales.atendidos, ...porDia.totales.valores]] : []),
                    ],
                }),
            ],
        }],
    };
}
